import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where, // <--- Added 'where' import here
} from "firebase/firestore";
import * as FirebaseConfig from "../config";
import { Notification } from "../../../interfaces/Notification";

/**
 * Creates a new notification for a specific user.
 * This is for non-message notifications like 'request_received'.
 */
export const createNotification = async (
  userId: string,
  type: string,
  message: string,
  resourcePath: string,
  relatedUserId?: string, // Add this for associating notifications with a user
) => {
  if (!userId) {
    return;
  }
  try {
    // Corrected path to include userId for user-specific notifications
    const notificationsCollectionRef = collection(
      FirebaseConfig.FIRESTORE_DB,
      `users/${userId}/notifications`, // CORRECTED PATH
    );
    await addDoc(notificationsCollectionRef, {
      type,
      message,
      resourcePath,
      isRead: false,
      timestamp: serverTimestamp(),
      relatedUserId,
    });
  } catch (error) {
  }
};

/**
 * Subscribes to a user's notification collection for real-time updates.
 */
export const subscribeToNotifications = (
  userId: string,
  onUpdate: (notifications: Notification[]) => void,
  onError: (error: Error) => void,
) => {
  if (!userId) {
    onError(new Error("User ID is required for notification subscription."));
    return () => {}; // Return an empty unsubscribe function
  }
  try {
    // Corrected path to include userId for user-specific notifications
    const notificationsCollectionRef = collection(
      FirebaseConfig.FIRESTORE_DB,
      `users/${userId}/notifications`, // CORRECTED PATH
    );
    const q = query(notificationsCollectionRef, orderBy("timestamp", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const fetchedNotifications: Notification[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const timestamp =
            data.timestamp instanceof Timestamp
              ? data.timestamp.toDate()
              : new Date();

          fetchedNotifications.push({
            id: doc.id,
            type: data.type,
            message: data.message,
            resourcePath: data.resourcePath,
            isRead: data.isRead,
            relatedUserId: data.relatedUserId,
            timestamp,
          });
        });
        onUpdate(fetchedNotifications);
      },
      (error) => {
        onError(error);
      },
    );
    return unsubscribe;
  } catch (error) {
    onError(error as Error);
    return () => {}; // Return an empty unsubscribe function on setup failure
  }
};

/**
 * Marks a specific notification as read.
 */
export const markNotificationAsRead = async (
  userId: string,
  notificationId: string,
) => {
  if (!userId || !notificationId) {
    return;
  }
  try {
    // Corrected path to include userId and notificationId
    const notificationRef = doc(
      FirebaseConfig.FIRESTORE_DB,
      `users/${userId}/notifications/${notificationId}`, // CORRECTED PATH
    );
    await updateDoc(notificationRef, { isRead: true });
  } catch (error) {
  }
};

/**
 * Updates or clears the push notification token for a user in their user document.
 * @param userId The ID of the user.
 * @param token The push token to store, or null to clear it.
 */
export const updateUserPushToken = async (
  userId: string,
  token: string | null,
) => {
  if (!userId) {
    return;
  }
  const userDocRef = doc(FirebaseConfig.FIRESTORE_DB, `users/${userId}`);
  try {
    await updateDoc(userDocRef, {
      pushToken: token, // Store the token
      lastTokenUpdate: token ? serverTimestamp() : null, // Optional: timestamp for last update
    });
  } catch (error) {
  }
};

/**
 * Subscribes to the count of unread notifications, excluding 'message_new' types.
 * This is intended for the general "bell" icon notification count.
 */
export const subscribeToBellNotificationCount = (
  userId: string,
  onCountUpdate: (count: number) => void,
  onError: (error: Error) => void,
) => {
  if (!userId) {
    onError(
      new Error(
        "User ID is required for bell notification count subscription.",
      ),
    );
    return () => {}; // Return an empty unsubscribe function
  }

  try {
    const notificationsCollectionRef = collection(
      FirebaseConfig.FIRESTORE_DB,
      `users/${userId}/notifications`,
    );

    // Query for unread notifications that are NOT of type 'message_new'
    const q = query(
      notificationsCollectionRef,
      where("isRead", "==", false),
      where("type", "!=", "message_new"), // Exclude message notifications
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        onCountUpdate(querySnapshot.size); // Pass the count of matching documents
      },
      (error) => {
        onError(error);
      },
    );
    return unsubscribe;
  } catch (error) {
    onError(error as Error);
    return () => {};
  }
};
